import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  BreadcrumbGroup,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
  Textarea,
  type TableProps,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { fetchTenant, updateTenant, deleteTenant } from '../api/tenants';
import { fetchTenantAuditLog, type AuditEntry } from '../api/audit';
import {
  fetchDeployments,
  createDeployment,
  updateDeployment,
  getLaunchUrl,
  validateDeployment as apiValidateDeployment,
  removeDeployment as apiRemoveDeployment,
  type RoleDeployment,
  type ProductDefinition,
  type DeploymentStatus,
} from '../api/deployments';
import { HealthDot } from '../components/HealthDot';
import { StatusBadge } from '../components/StatusBadge';
import type { Tenant, TenantProduct, TenantStatus } from '../types/tenant';

const PRODUCT_STATUS_MAP: Record<string, 'pending' | 'in-progress' | 'success' | 'error' | 'stopped'> = {
  pending: 'pending',
  deploying: 'in-progress',
  deployed: 'success',
  update_available: 'success',
  updating: 'in-progress',
  failed: 'error',
  removing: 'in-progress',
  removed: 'stopped',
};

const PRODUCT_COLUMNS: TableProps.ColumnDefinition<TenantProduct>[] = [
  { id: 'name', header: 'Product', cell: (item) => item.productName, width: 180 },
  { id: 'version', header: 'Version', cell: (item) => <code>{item.stackVersion}</code>, width: 100 },
  {
    id: 'status', header: 'Status', width: 140,
    cell: (item) => (
      <StatusIndicator type={PRODUCT_STATUS_MAP[item.status] ?? 'pending'}>
        {item.status.replace('_', ' ')}
      </StatusIndicator>
    ),
  },
  {
    id: 'onboarding', header: 'Onboarding', width: 130,
    cell: (item) => {
      if (item.onboardingStatus === 'completed') return <Badge color="green">Complete</Badge>;
      if (item.onboardingStatus === 'in_progress') return <Badge color="blue">Step {item.onboardingStep}/6</Badge>;
      if (item.onboardingStatus === 'blocked') return <Badge color="red">Blocked</Badge>;
      return <Badge color="grey">Not started</Badge>;
    },
  },
  { id: 'stack', header: 'Stack Name', cell: (item) => item.stackName || '—' },
  {
    id: 'hosting', header: 'Hosting', width: 110,
    cell: (item) => {
      if (!item.hostingMode) return '—';
      return item.hostingMode === 'saas-hosted' ? '☁️ SaaS' : '🏗️ Self';
    },
  },
  {
    id: 'account', header: 'AWS Account', width: 130,
    cell: (item) => item.customerAccountId ? <code style={{ fontSize: 12 }}>{item.customerAccountId}</code> : '—',
  },
  { id: 'deployed', header: 'Deployed', cell: (item) => item.deployedAt ? new Date(item.deployedAt).toLocaleDateString() : '—', width: 110 },
];

export function TenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    fetchTenant(tenantId)
      .then(setTenant)
      .catch((err) => console.error('Failed to load tenant:', err))
      .finally(() => setLoading(false));
  }, [tenantId]);

  async function handleStatusChange(newStatus: TenantStatus) {
    if (!tenantId) return;
    try {
      const updated = await updateTenant(tenantId, { status: newStatus });
      setTenant(updated);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  }

  async function handleDelete() {
    if (!tenantId || !tenant) return;
    const typed = prompt(`Type "${tenant.name}" to confirm deletion. This removes all deployments, products, and records.`);
    if (typed !== tenant.name) { alert('Name did not match. Delete cancelled.'); return; }
    try {
      await deleteTenant(tenantId, tenant.name);
      navigate('/ops/tenants');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete');
    }
  }

  if (loading) return <Box textAlign="center" padding="xxl">Loading tenant...</Box>;
  if (!tenant) return <Box textAlign="center" padding="xxl">Tenant not found.</Box>;

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup items={[{ text: 'Tenants', href: '/ops/tenants' }, { text: tenant.name, href: '#' }]} onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }} />

      <Header
        variant="h1"
        description={tenant.companyName}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => navigate(`/ops/deployments/${tenantId}`)}>Role Deployments</Button>
            {tenant.status === 'active' ? (
              <Button onClick={() => handleStatusChange('suspended')}>Disable Tenant</Button>
            ) : (
              <Button variant="primary" onClick={() => handleStatusChange('active')}>Activate Tenant</Button>
            )}
            <Button onClick={handleDelete}>Delete Tenant</Button>
          </SpaceBetween>
        }
      >
        {tenant.name}
      </Header>

      <SpaceBetween size="s" direction="horizontal">
        <StatusBadge status={tenant.status} />
        <HealthDot status={tenant.healthStatus} />
        <Badge>{tenant.subscriptionTier}</Badge>
      </SpaceBetween>

      {tenant.products?.some((p) => (p as unknown as Record<string, unknown>).pcLinkStatus === 'not_linked') && (
        <Alert type="warning">
          ⚠️ This account is not linked to Partner Central. PC3 features will not work until the customer links this AWS account to their PC identity.
        </Alert>
      )}

      <Tabs tabs={[
        {
          id: 'overview',
          label: 'Overview',
          content: (
            <ColumnLayout columns={2}>
              <Container header={<Header variant="h2">Tenant Info</Header>}>
                <SpaceBetween size="s">
                  <div><Box variant="awsui-key-label">AWS Account</Box><Box>{tenant.awsAccountId || 'Not connected'}</Box></div>
                  <div><Box variant="awsui-key-label">Region</Box><Box>{tenant.region}</Box></div>
                  <div><Box variant="awsui-key-label">Subscription</Box><Box>{tenant.subscriptionTier}</Box></div>
                  <div><Box variant="awsui-key-label">Contact</Box><Box>{tenant.contactEmail}</Box></div>
                  <div><Box variant="awsui-key-label">Created</Box><Box>{new Date(tenant.createdAt).toLocaleDateString()}</Box></div>
                  {tenant.marketplaceCustomerId && (
                    <div><Box variant="awsui-key-label">MP Customer ID</Box><Box><code>{tenant.marketplaceCustomerId}</code></Box></div>
                  )}
                </SpaceBetween>
              </Container>
              <Container header={<Header variant="h2">Notes</Header>}>
                <Box>{tenant.notes || 'No notes.'}</Box>
              </Container>
            </ColumnLayout>
          ),
        },
        {
          id: 'products',
          label: `Products (${tenant.products?.length || 0})`,
          content: (
            <Table
              columnDefinitions={PRODUCT_COLUMNS}
              items={tenant.products || []}
              trackBy="productId"
              variant="embedded"
              stripedRows
              empty={<Box textAlign="center">No products deployed to this tenant yet.</Box>}
              header={<Header variant="h2">Deployed Products</Header>}
            />
          ),
        },
        {
          id: 'health',
          label: 'Health',
          content: <Container header={<Header variant="h2">Health</Header>}><Box>Health monitoring coming in Phase 2.</Box></Container>,
        },
        {
          id: 'deployments',
          label: 'Role Deployments',
          content: <InlineRoleDeployments tenantId={tenantId || ''} />,
        },
        {
          id: 'audit',
          label: 'Audit Trail',
          content: <TenantAuditTrail tenantId={tenantId || ''} />,
        },
      ]} />
    </SpaceBetween>
  );
}

const STATUS_LABEL: Record<DeploymentStatus, { type: 'pending' | 'in-progress' | 'success' | 'error' | 'stopped'; label: string }> = {
  pending: { type: 'pending', label: 'Pending' },
  parameters_set: { type: 'in-progress', label: 'Ready to Deploy' },
  cfn_launched: { type: 'in-progress', label: 'Stack Launched' },
  deployed: { type: 'success', label: 'Deployed' },
  integrated: { type: 'success', label: 'Integrated' },
  validation_failed: { type: 'error', label: 'Validation Failed' },
  failed: { type: 'error', label: 'Failed' },
  removed: { type: 'stopped', label: 'Removed' },
};

function InlineRoleDeployments({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const [deployments, setDeployments] = useState<RoleDeployment[]>([]);
  const [catalog, setCatalog] = useState<Record<string, ProductDefinition>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addProductId, setAddProductId] = useState<string | null>(null);
  const [addParams, setAddParams] = useState<Record<string, string>>({});
  const [addNotes, setAddNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function loadDeployments(): void {
    if (!tenantId) return;
    setLoading(true);
    fetchDeployments(tenantId)
      .then((res) => { setDeployments(res.deployments); setCatalog(res.catalog); })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadDeployments(); }, [tenantId]);

  const availableProducts = Object.values(catalog).filter(
    (p) => !deployments.some((d) => d.productId === p.productId)
  );
  const selectedProductDef = addProductId ? catalog[addProductId] : null;

  async function handleCreate(): Promise<void> {
    if (!tenantId || !addProductId) return;
    setSubmitting(true);
    try {
      const dep = await createDeployment(tenantId, addProductId, addParams, addNotes || undefined);
      setDeployments((prev) => [...prev, dep]);
      setShowAddModal(false);
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed'); }
    finally { setSubmitting(false); }
  }

  async function handleValidate(dep: RoleDeployment): Promise<void> {
    const acct = dep.stackOutputs?.customerAccountId || '';
    const input = prompt(`12-digit AWS Account ID where ${dep.productName} role was deployed:`, acct);
    if (!input || !/^\d{12}$/.test(input.trim())) { setError('Valid 12-digit account ID required'); return; }
    try {
      const result = await apiValidateDeployment(tenantId, dep.productId, input.trim());
      if (!result.validated) setError(result.message);
      else setError(null);
      loadDeployments();
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Validation failed'); }
  }

  async function handleLaunchStack(dep: RoleDeployment): Promise<void> {
    try {
      const result = await getLaunchUrl(tenantId, dep.productId);
      if (result.launchUrl) {
        window.open(result.launchUrl, '_blank');
        await updateDeployment(tenantId, dep.productId, { status: 'cfn_launched' });
        loadDeployments();
      } else { setError('No template available.'); }
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed'); }
  }

  async function handleRemove(dep: RoleDeployment): Promise<void> {
    if (!confirm(`Remove ${dep.productName} role deployment? This action is audited.`)) return;
    try {
      await apiRemoveDeployment(tenantId, dep.productId);
      setDeployments((prev) => prev.filter((d) => d.productId !== dep.productId));
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed'); }
  }

  const columns: TableProps.ColumnDefinition<RoleDeployment>[] = [
    { id: 'product', header: 'Product', cell: (item) => item.productName, width: 150 },
    { id: 'vendor', header: 'Vendor', cell: (item) => item.vendor === 'gdna' ? 'GDNA' : '3rd Party', width: 80 },
    {
      id: 'status', header: 'Status', width: 140,
      cell: (item) => {
        const s = STATUS_LABEL[item.status];
        return <StatusIndicator type={s?.type ?? 'pending'}>{s?.label ?? item.status}</StatusIndicator>;
      },
    },
    {
      id: 'account', header: 'AWS Account', width: 130,
      cell: (item) => item.stackOutputs?.customerAccountId
        ? <code style={{ fontSize: 12 }}>{item.stackOutputs.customerAccountId}</code>
        : '—',
    },
    {
      id: 'role', header: 'Role', width: 160,
      cell: (item) => item.stackOutputs?.roleName || item.stackOutputs?.roleArn?.split('/').pop() || '—',
    },
    {
      id: 'validated', header: 'Validated', width: 100,
      cell: (item) => item.validated
        ? <Badge color="green">✓ {item.validatedAt ? new Date(item.validatedAt).toLocaleDateString() : ''}</Badge>
        : <Badge color="grey">No</Badge>,
    },
    {
      id: 'actions', header: 'Actions', width: 220,
      cell: (item) => {
        const isPre = item.status === 'parameters_set' || item.status === 'pending';
        const isLaunched = item.status === 'cfn_launched';
        const needsValidation = item.status === 'deployed' || item.status === 'validation_failed';
        const isDone = item.status === 'integrated';
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            {isPre && <Button variant="primary" iconName="external" onClick={() => handleLaunchStack(item)}>Launch</Button>}
            {isLaunched && <Button variant="primary" onClick={() => updateDeployment(tenantId, item.productId, { status: 'deployed' }).then(() => loadDeployments())}>Confirm</Button>}
            {needsValidation && <Button variant="primary" onClick={() => handleValidate(item)}>Validate</Button>}
            {isDone && <Badge color="green">Complete</Badge>}
            <ButtonDropdown
              items={[
                { id: 'remove', text: 'Remove' },
              ]}
              onItemClick={({ detail }) => { if (detail.id === 'remove') handleRemove(item); }}
              variant="icon"
            />
          </SpaceBetween>
        );
      },
    },
  ];

  return (
    <SpaceBetween size="s">
      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
      <Table
        columnDefinitions={columns}
        items={deployments}
        loading={loading}
        loadingText="Loading role deployments..."
        trackBy="productId"
        variant="embedded"
        stripedRows
        empty={
          <Box textAlign="center" padding="l">
            <Box variant="p" color="text-status-inactive">No role deployments yet.</Box>
            <Button variant="primary" onClick={() => setShowAddModal(true)}>Add Role Deployment</Button>
          </Box>
        }
        header={
          <Header
            counter={`(${deployments.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={loadDeployments}>Refresh</Button>
                <Button variant="primary" onClick={() => { setAddProductId(null); setAddParams({}); setAddNotes(''); setShowAddModal(true); }} disabled={availableProducts.length === 0}>
                  Add Role Deployment
                </Button>
                <Button onClick={() => navigate(`/ops/deployments/${tenantId}`)}>Full View →</Button>
              </SpaceBetween>
            }
          >
            Role Deployments
          </Header>
        }
      />

      <Modal visible={showAddModal} onDismiss={() => setShowAddModal(false)} header="Add Role Deployment"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} loading={submitting} disabled={!addProductId}>Create</Button>
        </SpaceBetween></Box>}>
        <SpaceBetween size="m">
          <FormField label="Product">
            <select value={addProductId || ''} onChange={(e) => { setAddProductId(e.target.value || null); setAddParams({}); }}
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #aab7b8' }}>
              <option value="">Choose a product...</option>
              {availableProducts.map((p) => <option key={p.productId} value={p.productId}>{p.productName}</option>)}
            </select>
          </FormField>
          {selectedProductDef && (<>
            <Alert type="info">{selectedProductDef.description}</Alert>
            {selectedProductDef.externalUrl && <Alert type="warning">Create tenant at <Link external href={selectedProductDef.externalUrl}>{selectedProductDef.externalUrl}</Link> first, then paste parameters below.</Alert>}
            {selectedProductDef.requiredParameters.filter((p) => !p.autoGenerated).map((param) => (
              <FormField key={param.name} label={param.label} description={param.description}>
                <Input value={addParams[param.name] || ''} onChange={({ detail }) => setAddParams((prev) => ({ ...prev, [param.name]: detail.value }))} />
              </FormField>
            ))}
            <FormField label="Notes"><Textarea value={addNotes} onChange={({ detail }) => setAddNotes(detail.value)} rows={2} /></FormField>
          </>)}
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}

const ACTION_LABELS: Record<string, string> = {
  TENANT_CREATED: 'Tenant Created',
  TENANT_UPDATED: 'Tenant Updated',
  DEPLOYMENT_CREATED: 'Role Deployment Added',
  DEPLOYMENT_VALIDATED: 'Role Validated',
  DEPLOYMENT_REMOVED: 'Role Deployment Removed',
};

function TenantAuditTrail({ tenantId }: { tenantId: string }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    fetchTenantAuditLog(tenantId)
      .then((res) => setEntries(res.entries))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tenantId]);

  return (
    <Table
      columnDefinitions={[
        {
          id: 'time', header: 'Time', width: 180,
          cell: (item) => new Date(item.timestamp).toLocaleString(),
        },
        {
          id: 'action', header: 'Action', width: 200,
          cell: (item) => (
            <StatusIndicator type={item.action.includes('REMOVED') ? 'error' : item.action.includes('VALIDATED') ? 'success' : 'info'}>
              {ACTION_LABELS[item.action] || item.action}
            </StatusIndicator>
          ),
        },
        { id: 'product', header: 'Product', width: 150, cell: (item) => item.productId || '—' },
        { id: 'actor', header: 'Actor', width: 150, cell: (item) => item.actor },
        {
          id: 'detail', header: 'Details',
          cell: (item) => {
            const d = item.detail;
            const parts: string[] = [];
            if (d.customerAccountId) parts.push(`Account: ${d.customerAccountId}`);
            if (d.roleName) parts.push(`Role: ${d.roleName}`);
            if (d.name) parts.push(`Name: ${d.name}`);
            if (d.companyName) parts.push(`Company: ${d.companyName}`);
            if (d.updatedFields) parts.push(`Fields: ${(d.updatedFields as string[]).join(', ')}`);
            if (d.method) parts.push(`Method: ${d.method}`);
            return parts.join(' · ') || '—';
          },
        },
      ]}
      items={entries}
      loading={loading}
      loadingText="Loading audit trail..."
      trackBy="auditId"
      variant="embedded"
      stripedRows
      sortingDisabled
      empty={<Box textAlign="center" padding="l" color="text-status-inactive">No audit entries for this tenant yet.</Box>}
      header={<Header counter={`(${entries.length})`}>Audit Trail</Header>}
    />
  );
}
